import React, { useState, useRef, useEffect } from "react";
import {
    Clock,
    Hourglass,
    RefreshCw,
    Phone,
    AlertTriangle,
    Crown,
    ArrowUpRight,
    ArrowDownRight,
    ChevronDown,
} from "lucide-react";
import {
    BarChart,
    Bar,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip as RechartsTooltip,
    ResponsiveContainer,
    Cell,
    PieChart,
    Pie,
    AreaChart,
    Area,
} from "recharts";

const CHART_PALETTE = [
    "#4F46E5", // Indigo
    "#10B981", // Emerald
    "#F59E0B", // Amber
    "#0EA5E9", // Sky
    "#8B5CF6", // Violet
    "#EC4899", // Pink
];

const COLORS = {
    bg: "#F6F8FC",
    card: "#FFFFFF",
    border: "#E7EBF3",
    heading: "#0F172A",
    text: "#3B4457",
    muted: "#8896A8",
    primary: "#4F46E5",
    primarySoft: "#EEF2FF",
    success: "#16A34A",
    successSoft: "#ECFDF3",
    warning: "#F59E0B",
    warningSoft: "#FFFBEB",
    danger: "#EF4444",
    dangerSoft: "#FEF2F2",
    info: "#0EA5E9",
    infoSoft: "#EFF8FF",
    purple: "#8B5CF6",
    purpleSoft: "#F5F3FF",
    teal: "#0D9488",
    tealSoft: "#F0FDFA",
    track: "#EEF1F6",
};

const KPI_DATA = [
    {
        icon: <Clock size={16} />,
        color: COLORS.primary,
        soft: COLORS.primarySoft,
        label: "Avg Response Time",
        value: "1.2 Hrs",
        trendDir: "down",
        trendGood: true,
        trendLabel: "14% imp.",
    },
    {
        icon: <Hourglass size={16} />,
        color: COLORS.warning,
        soft: COLORS.warningSoft,
        label: "Lead Age (Avg)",
        value: "4.5 Days",
        trendDir: "up",
        trendGood: false,
        trendLabel: "0.2% inc.",
    },
    {
        icon: <RefreshCw size={16} />,
        color: COLORS.teal,
        soft: COLORS.tealSoft,
        label: "Conversion %",
        value: "24.8%",
        trendDir: "up",
        trendGood: true,
        trendLabel: "4.3%",
    },
    {
        icon: <Phone size={16} />,
        color: COLORS.info,
        soft: COLORS.infoSoft,
        label: "Total Activities",
        value: "1,240",
        trendDir: "up",
        trendGood: true,
        trendLabel: "4.3%",
        // subLabel: "Meetings",
        // subValue: "86",
    },
];

const REVENUE_BY_EMPLOYEE = [
    { name: "Alex", value: 62, hero: true },
    { name: "Sarah", value: 38, hero: false },
    { name: "Mike", value: 78, hero: true },
    { name: "John", value: 34, hero: false },
    { name: "Elena", value: 58, hero: true },
    { name: "Other", value: 22, hero: false },
];

const TARGET_ACHIEVEMENT = 78;
const QUARTERLY_GOAL_VALUE = 8500000; // ₹85,00,000
const ACHIEVED_VALUE = Math.round((QUARTERLY_GOAL_VALUE * TARGET_ACHIEVEMENT) / 100);
const REMAINING_VALUE = QUARTERLY_GOAL_VALUE - ACHIEVED_VALUE;
const QUARTERLY_GOAL = "₹" + QUARTERLY_GOAL_VALUE.toLocaleString("en-IN");

function formatINR(num) {
    return "₹" + num.toLocaleString("en-IN");
}
function formatLakh(num) {
    return "₹" + (num / 100000).toFixed(1) + "L";
}

const LOST_REASONS = [
    { label: "Price Too High", value: 42, color: "#EF4444" },          // Red (Danger)
    { label: "Lost to Competitor", value: 35, color: "#F59E0B" },      // Amber (Warning)
    { label: "Missing Feature", value: 28, color: "#8B5CF6" },         // Purple
    { label: "Ghosted / Unresponsive", value: 20, color: "#6B7280" },   // Muted Slate Gray
    { label: "Budget Frozen", color: "#06B6D4", value: 15 },           // Cyan / Teal
    { label: "No Decision Maker", color: "#EC4899", value: 10 },       // Pink / Magenta
];

const PIPELINE_RISK = [
    { deal: "Enterprise ERP Proposal", value: "₹32,00,000", owner: "Mike Dean", idle: "11 Days", status: "High Risk" },
    { deal: "SaaS Annual Contract", value: "₹42,50,000", owner: "Elena R.", idle: "8 Days", status: "High Risk" },
    { deal: "Logistics Solution", value: "₹8,40,000", owner: "Sarah J.", idle: "6 Days", status: "Warning" },
];

const STATUS_STYLE = {
    "High Risk": { color: COLORS.danger, soft: COLORS.dangerSoft },
    Warning: { color: COLORS.warning, soft: COLORS.warningSoft },
};

const TOP_PERFORMERS = [
    { name: "Mike Dean", initials: "MD", revenue: "₹45.2L" },
    { name: "Alex Wong", initials: "AW", revenue: "₹38.1L" },
    { name: "Elena Ross", initials: "ER", revenue: "₹32.5L" },
];

function RangeDropdown({ value, onChange, options = ["Weekly", "Monthly", "Yearly"] }) {
    const [open, setOpen] = useState(false);
    const wrapRef = useRef(null);

    useEffect(() => {
        if (!open) return;
        const handleClick = (e) => {
            if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
        };
        document.addEventListener("mousedown", handleClick);
        return () => document.removeEventListener("mousedown", handleClick);
    }, [open]);

    return (
        <div className="position-relative" ref={wrapRef}>
            <button
                type="button"
                className="btn btn-sm btn-light border d-flex align-items-center gap-2"
                style={{ fontSize: '11px', fontWeight: '600', borderRadius: '8px', padding: '12px 40px' }}
                onClick={() => setOpen(!open)}
            >
                {value}
                <ChevronDown size={12} style={{ transition: 'transform 0.2s', transform: open ? 'rotate(180deg)' : 'none' }} />
            </button>
            {open && (
                <div className="position-absolute bg-white border rounded shadow-lg p-1" style={{ top: '100%', right: 0, zIndex: 100, minWidth: '110px', marginTop: '4px' }}>
                    {options.map((opt) => (
                        <div
                            key={opt}
                            className="dropdown-item-custom"
                            style={{
                                fontSize: '11px',
                                padding: '6px 10px',
                                cursor: 'pointer',
                                borderRadius: '6px',
                                background: value === opt ? COLORS.primarySoft : 'transparent',
                                color: value === opt ? COLORS.primary : COLORS.text,
                                fontWeight: value === opt ? '700' : '500'
                            }}
                            onClick={() => {
                                onChange(opt);
                                setOpen(false);
                            }}
                            onMouseEnter={(e) => { if (value !== opt) e.target.style.background = '#f8f9fa' }}
                            onMouseLeave={(e) => { if (value !== opt) e.target.style.background = 'transparent' }}
                        >
                            {opt}
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}

function SectionCard({ title, subtitle, icon, accent = COLORS.primary, action, children, style, titleColor }) {
    return (
        <div className="sd-card h-100" style={style}>
            {(title || icon) && (
                <div className="sd-card-head">
                    <div className="sd-card-head-left">
                        <div>
                            <div className="sd-card-title" style={titleColor ? { color: titleColor } : undefined}>{title}</div>
                            {subtitle && <div className="sd-card-subtitle">{subtitle}</div>}
                        </div>
                    </div>
                    <div className="sd-card-head-right">
                        {action}
                        {icon && (
                            <span className="sd-card-head-icon" style={{ background: `${accent}17`, color: accent }}>
                                {icon}
                            </span>
                        )}
                    </div>
                </div>
            )}
            <div className="sd-card-body">{children}</div>
        </div>
    );
}

function KpiCard({ d, i }) {
    const trendColor = d.trendLabel ? (d.trendGood ? COLORS.success : COLORS.danger) : null;
    const sparklineData = [
        { val: 12 }, { val: 18 }, { val: 14 }, { val: 22 }, { val: 19 }, { val: 28 }, { val: 24 }
    ];
    return (
        <div className="sd-card p-3 h-100 transition-all shadow-sm">
            <div className='d-flex justify-content-between align-items-end position-relative'>
                <div className="pt-3 text-muted" style={{ fontSize: "12.5px" }}>
                    <div className="d-flex align-items-start justify-content-between mb-3">
                        <div className="sd-kpi-icon" style={{ background: d.soft, color: d.color }}>
                            {d.icon}
                        </div>
                        {d.trendLabel && (
                            <div className="sd-badge position-absolute" style={{ right: 0, top: 10, color: trendColor, background: d.trendGood ? COLORS.successSoft : COLORS.dangerSoft, fontSize: '10px' }}>
                                {d.trendDir === "up" ? "▲" : "▼"} {d.trendLabel}
                            </div>
                        )}
                    </div>
                    <div className="sd-kpi-label text-uppercase mb-1" style={{ fontSize: '10px', letterSpacing: '0.05em', opacity: 0.8 }}>{d.label}</div>
                    <h3 className="fw-800 text-dark mb-1" style={{ fontSize: '22px', letterSpacing: '-0.02em' }}>{d.value}</h3>

                </div>
                {
                    i === 0 &&
                    <div style={{ width: "100px", height: "45px" }}>
                        <ResponsiveContainer width="100%" height="100%">
                            <AreaChart data={sparklineData}>
                                <defs>
                                    <linearGradient id={`gradient-up`} x1="0" y1="0" x2="0" y2="1">
                                        <stop offset="0%" stopColor={'#16a34a'} stopOpacity={0.25} />
                                        <stop offset="100%" stopColor={'#16a34a'} stopOpacity={0.0} />
                                    </linearGradient>
                                </defs>
                                <Area
                                    type="monotone"
                                    dataKey="val"
                                    stroke={'#16a34a'}
                                    strokeWidth={1.8}
                                    fillOpacity={1}
                                    fill={`url(#gradient-up)`}
                                    isAnimationActive={true}
                                />
                            </AreaChart>
                        </ResponsiveContainer>
                    </div>
                }
            </div>

            {d.subValue && (
                <div className="d-flex align-items-center gap-2 pt-2 mt-2 border-top">
                    <span className="fw-700 text-dark" style={{ fontSize: '12px' }}>{d.subValue}</span>
                    <span className="sd-card-subtitle" style={{ fontSize: '11px', marginTop: 0 }}>{d.subLabel}</span>
                </div>
            )}
        </div>
    );
}

export const SalesManagerDashboard = () => {
    const [revenueRange, setRevenueRange] = useState("Monthly");
    const donutData = [
        { name: "Achieved", value: TARGET_ACHIEVEMENT, amount: ACHIEVED_VALUE },
        { name: "Remaining", value: 100 - TARGET_ACHIEVEMENT, amount: REMAINING_VALUE },
    ];

    return (
        <div className="sd-root mt-4">
            <style>{CSS}</style>
            <div className="row g-3 mb-4">
                {KPI_DATA.map((d, i) => (
                    <div className="col-lg-3" key={i}>
                        <KpiCard d={d} i={i} />
                    </div>
                ))}
            </div>

            <div className="row g-3 mb-4">
                <div className="col-lg-5">
                    <SectionCard
                        title="Revenue by Employee"
                        subtitle="Individual contribution"
                        action={<RangeDropdown value={revenueRange} onChange={setRevenueRange} />}
                    >
                        {/* SUGGESTION: I have made changes here to add a distributed multi-color bar chart and legend */}
                        <div style={{ height: 250 }}>
                            <ResponsiveContainer width="100%" height="100%">
                                <BarChart data={REVENUE_BY_EMPLOYEE} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
                                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f0f0f0" />
                                    <XAxis
                                        dataKey="name"
                                        tick={{ fontSize: 12, fill: COLORS.muted, fontWeight: 500 }}
                                        axisLine={false}
                                        tickLine={false}
                                        dy={10}
                                    />
                                    <YAxis tick={{ fontSize: 11, fill: COLORS.muted }} axisLine={false} tickLine={false} />
                                    <RechartsTooltip
                                        cursor={{ fill: '#f8f9fa' }}
                                        content={({ active, payload }) => {
                                            if (active && payload && payload.length) {
                                                return (
                                                    <div className="bg-white p-2 border rounded shadow-sm">
                                                        <div className="fw-800 mb-1" style={{ fontSize: '12px' }}>{payload[0].payload.name}</div>
                                                        <div className="text-primary fw-700" style={{ fontSize: '12px' }}>₹{payload[0].value}L Revenue</div>
                                                    </div>
                                                );
                                            }
                                            return null;
                                        }}
                                    />
                                    <Bar
                                        dataKey="value"
                                        radius={[4, 4, 0, 0]}
                                        barSize={Math.max(40, Math.min(90, 360 / REVENUE_BY_EMPLOYEE.length))}
                                    >
                                        {REVENUE_BY_EMPLOYEE.map((entry, index) => (
                                            <Cell key={`cell-${index}`} fill={CHART_PALETTE[index % CHART_PALETTE.length]} fillOpacity={0.9} />
                                        ))}
                                    </Bar>
                                </BarChart>
                            </ResponsiveContainer>
                        </div>
                        <div className="d-flex flex-wrap gap-3 justify-content-center mt-3 pt-2">
                            {REVENUE_BY_EMPLOYEE.map((e, i) => (
                                <div key={i} className="d-flex align-items-center gap-2" style={{ fontSize: '11px' }}>
                                    <div style={{ width: 8, height: 8, borderRadius: '50%', background: CHART_PALETTE[i % CHART_PALETTE.length] }}></div>
                                    <span className="fw-600 text-muted">{e.name}</span>
                                </div>
                            ))}
                        </div>
                    </SectionCard>
                </div>

                <div className="col-lg-3">
                    <SectionCard title="Target Achievement" subtitle="Quarterly progress">
                        <div className="position-relative d-flex flex-column align-items-center h-100">
                            <div style={{ width: "100%", height: 170, position: "relative" }}>
                                <ResponsiveContainer>
                                    <PieChart>
                                        <Pie
                                            data={donutData}
                                            dataKey="value"
                                            innerRadius={55}
                                            outerRadius={75}
                                            startAngle={90}
                                            endAngle={-270}
                                            stroke="none"
                                        >
                                            <Cell fill={COLORS.primary} />
                                            <Cell fill={COLORS.track} />
                                        </Pie>
                                        <RechartsTooltip
                                            position={{ x: 0, y: 0 }}
                                            wrapperStyle={{ zIndex: 20 }}
                                            content={({ active, payload }) => {
                                                if (active && payload && payload.length) {
                                                    const p = payload[0];
                                                    return (
                                                        <div className="bg-white px-2 py-1 border rounded shadow-sm" style={{ whiteSpace: 'nowrap' }}>
                                                            <div className="d-flex align-items-center gap-2" style={{ fontSize: '12px' }}>
                                                                <span style={{ width: 8, height: 8, borderRadius: '50%', background: p.payload.name === 'Achieved' ? COLORS.primary : COLORS.track, display: 'inline-block', flexShrink: 0 }}></span>
                                                                <span className="fw-600">{p.payload.name}</span>
                                                                <span className="fw-800 text-dark">{formatINR(p.payload.amount)}</span>
                                                            </div>
                                                        </div>
                                                    );
                                                }
                                                return null;
                                            }}
                                        />
                                    </PieChart>
                                </ResponsiveContainer>
                                <div className="position-absolute inset-0 d-flex flex-column align-items-center justify-content-center" style={{ top: '50%', left: '50%', transform: 'translate(-50%, -50%)' }}>
                                    <div className="fw-800 text-dark" style={{ fontSize: '20px', lineHeight: 1.1 }}>{formatLakh(ACHIEVED_VALUE)}</div>
                                    <div className="sd-card-subtitle" style={{ marginTop: 2 }}>{TARGET_ACHIEVEMENT}% of Goal</div>
                                </div>
                            </div>

                            <div className="d-flex align-items-center justify-content-center gap-3 mt-1" style={{ fontSize: '11px' }}>
                                <div className="d-flex align-items-center gap-2">
                                    <span style={{ width: 8, height: 8, borderRadius: '50%', background: COLORS.primary, display: 'inline-block' }}></span>
                                    <span className="fw-600 text-muted">Achieved</span>
                                    <span className="fw-800 text-dark">{formatLakh(ACHIEVED_VALUE)}</span>
                                </div>
                                <div className="d-flex align-items-center gap-2">
                                    <span style={{ width: 8, height: 8, borderRadius: '50%', background: COLORS.track, display: 'inline-block' }}></span>
                                    <span className="fw-600 text-muted">Remaining</span>
                                    <span className="fw-800 text-dark">{formatLakh(REMAINING_VALUE)}</span>
                                </div>
                            </div>

                            <div className="sd-achievement-badge w-100 mt-auto">
                                Quarterly Goal: <span className="fw-800">{QUARTERLY_GOAL}</span>
                            </div>
                        </div>
                    </SectionCard>
                </div>

                <div className="col-lg-4">
                    <SectionCard title="Lost Deal Reasons" subtitle="Top dropout factors" icon={<AlertTriangle size={16} />} accent={COLORS.muted}>
                        <div className="d-flex flex-column gap-3 mt-2">
                            {LOST_REASONS.map((r, i) => (
                                <div key={i}>
                                    <div className="d-flex align-items-center justify-content-between mb-1" style={{ fontSize: '13px' }}>
                                        <span className="fw-600">{r.label}</span>
                                        <span className="text-dark fw-700">{r.value}%</span>
                                    </div>
                                    <div className="sd-activity-track">
                                        <div className="sd-activity-fill" style={{ width: `${r.value}%`, background: r.color }} />
                                    </div>
                                </div>
                            ))}
                        </div>
                    </SectionCard>
                </div>
            </div>

            <div className="row g-3 mb-4">
                <div className="col-lg-8">
                    <SectionCard
                        title="Pipeline Intervention Required"
                        subtitle="High risk deals needing attention"
                        titleColor={COLORS.danger}
                        icon={<AlertTriangle size={16} />}
                        accent={COLORS.danger}
                    >
                        <div className="sd-table-wrap">
                            <table className="sd-table">
                                <thead>
                                    <tr>
                                        <th>Deal Name</th>
                                        <th>Value</th>
                                        <th>Owner</th>
                                        <th>Idle</th>
                                        <th>Status</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {PIPELINE_RISK.map((r, i) => (
                                        <tr key={i}>
                                            <td className="fw-600" style={{ color: COLORS.primary }}>{r.deal}</td>
                                            <td className="sd-table-strong">{r.value}</td>
                                            <td className="sd-table-muted">{r.owner}</td>
                                            <td style={{ color: COLORS.danger, fontWeight: 700 }}>{r.idle}</td>
                                            <td>
                                                <span
                                                    className="sd-badge"
                                                    style={{ color: STATUS_STYLE[r.status].color, background: STATUS_STYLE[r.status].soft }}
                                                >
                                                    {r.status}
                                                </span>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </SectionCard>
                </div>

                <div className="col-lg-4">
                    <SectionCard title="Top Performers" subtitle="Best revenue makers">
                        <div className="d-flex flex-column gap-1">
                            {TOP_PERFORMERS.map((p, i) => (
                                <div className="d-flex align-items-center gap-3 p-2 rounded-3" key={i} style={{ background: i === 0 ? COLORS.primarySoft : 'transparent' }}>
                                    <span className="fw-800 text-muted" style={{ width: '20px' }}>{i + 1}</span>
                                    <div className="sd-avatar" style={{ background: i === 0 ? COLORS.primary : COLORS.infoSoft, color: i === 0 ? '#fff' : COLORS.info }}>{p.initials}</div>
                                    <div className="flex-grow-1">
                                        <div className="sd-table-strong">{p.name}</div>
                                        <div className="sd-table-muted" style={{ fontSize: '12px' }}>{p.revenue}</div>
                                    </div>
                                    {i === 0 && <Crown size={16} color={COLORS.warning} fill={COLORS.warning} />}
                                </div>
                            ))}
                        </div>
                    </SectionCard>
                </div>
            </div>
        </div >
    );
}

const CSS = `
@import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800&display=swap');

.sd-root {
  font-family: 'Inter', sans-serif;
  background: #f3f3f3;
  height: 100%;
  color: #3B4457;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.sd-shell { max-width: 1680px; margin: 0 auto; width: 100%; }

.sd-card {
  background: #fff;
  border: 1px solid #0000002d;
  border-radius: 10px;
  display: flex; 
  flex-direction: column;
}

.sd-card-head { 
  display: flex; 
  align-items: flex-start; 
  justify-content: space-between; 
  gap: 12px; 
  padding: 15px 15px 4px; 
  flex-wrap: wrap; 
  border-bottom: 1px solid #7474742e; 
}

.sd-card-title { font-size: 16px; font-weight: 400; line-height: 1.5rem; }
.sd-card-subtitle { font-size: 12.5px; color: #8896A8; margin-top: 2px; }
.sd-card-body { padding: 16px; flex: 1; }

.sd-kpi-icon { 
  width: 36px; 
  height: 36px; 
  border-radius: 11px; 
  display: flex; 
  align-items: center; 
  justify-content: center; 
}

.sd-kpi-label { font-size: 12.5px; font-weight: 600; color: #3B4457; margin-top: 2px; }
.sd-kpi-trend-mini { font-size: 11px; font-weight: 300; display: inline-flex; align-items: center; gap: 3px; }

.sd-table-wrap { overflow-x: auto; }
.sd-table { width: 100%; border-collapse: collapse; font-size: 13px; }
.sd-table thead th { 
  text-align: left; 
  font-size: 11px; 
  text-transform: uppercase; 
  letter-spacing: .04em; 
  color: #8896A8; 
  font-weight: 700; 
  padding: 12px; 
  border-bottom: 1px solid #EEF1F6;
}
.sd-table tbody td { padding: 14px 12px; border-bottom: 1px solid #EEF1F6; white-space: nowrap; }
.sd-table-strong { font-weight: 600; color: #0F172A; }
.sd-table-muted { color: #8896A8; }

.sd-badge { display: inline-flex; align-items: center; font-size: 12px; font-weight: 700; padding: 4px 10px; border-radius: 999px; }
.sd-avatar { width: 32px; height: 32px; border-radius: 10px; display: inline-flex; align-items: center; justify-content: center; font-size: 11.5px; font-weight: 700; }

.sd-activity-track { height: 6px; border-radius: 999px; background: #EEF1F6; overflow: hidden; }
.sd-activity-fill { height: 100%; border-radius: 999px; transition: width .7s ease; }

.sd-achievement-badge { 
  font-size: 12px; 
  font-weight: 600; 
  color: #16A34A; 
  background: #ECFDF3; 
  padding: 8px 10px; 
  border-radius: 10px; 
  text-align: center; 
}
`;